// src/components/AdminAnnouncements/AdminAnnouncementsCreate.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FilledInput,
  Snackbar,
  Alert,
} from "@mui/material";

const AdminAnnouncementsCreate = ({ open, onClose, adminOwnerId }) => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expireDate, setExpireDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });

  // Reset form fields
  const resetForm = () => {
    setSubject("");
    setDescription("");
    setStartDate("");
    setExpireDate("");
  };

  // Handle form submission
  const handleSubmit = async () => {
    // 1) Ensure subject and description aren’t empty
    if (!subject.trim() || !description.trim()) {
      setSnack({
        open: true,
        msg: "Subject and description are required.",
        sev: "error",
      });
      return;
    }
    if (description && description.length > 1000) {
      setSnack({
        open: true,
        msg: "Description cannot exceed 1,000 characters.",
        sev: "error",
      });
      return;
    }

    if (!expireDate) {
      setSnack({
        open: true,
        msg: "Expire date is required.",
        sev: "error",
      });
      return;
    }
  
    // We’ll compare dates against "today" at 00:00:00
    const today = new Date();
    today.setHours(0, 0, 0, 0);
  

    
    
  
    if (expireDate) {
      // Manually parse the string (assumed format "YYYY-MM-DD") as a local date.
      const parts = expireDate.split("-"); // e.g., ["2025", "04", "15"]
      const eDate = new Date(parts[0], parts[1] - 1, parts[2]); // local date object
      // 'today' should already be set to today's date at midnight:
      // const today = new Date();
      // today.setHours(0, 0, 0, 0);
      
      if (eDate < today) {
        setSnack({
          open: true,
          msg: "Expire date cannot be in the past.",
          sev: "error",
        });
        return;
      }
    }
    
  
    // 4) Expire date must be after start date
    if (startDate && expireDate && new Date(expireDate) < new Date(startDate)) {
      setSnack({
        open: true,
        msg: "Expire date must be after start date.",
        sev: "error",
      });
      return;
    }
  
    setSubmitting(true);
    try {
      // Submit the announcement
      const res = await axios.post("/admin/submitAnnouncement", {
        announcement: subject,
        description,
        startDate,
        expireDate,
        adminOwnerId,
      });
      if (res.data.success) {
        setSnack({ open: true, msg: "Announcement created.", sev: "success" });
        resetForm();      // Clear local state fields
        onClose(true);    // Signal parent to refresh
      } else {
        setSnack({
          open: true,
          msg: res.data.message || "Failed to create announcement.",
          sev: "error",
        });
      }
    } catch {
      setSnack({
        open: true,
        msg: "Server error creating announcement.",
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };
  

  return (
    <>
      <Dialog open={open} onClose={() => onClose(false)} maxWidth={false}>
        {/* Title */}
        <DialogTitle className="mx-auto text-2xl font-bold font-brand-primary">
          NEW ANNOUNCEMENT
        </DialogTitle>

        {/* Content */}
        <DialogContent className="flex flex-col gap-4 mb-4 mx-4">
          {/* Subject */}
          <div className="flex flex-col w-96">
            <label className="text-lg font-brand-primary mb-1">Subject *</label>
            <FilledInput
              type="text"
              disableUnderline
              placeholder="Announcement Subject"
              required
              value={subject}
              maxLength={100}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-xl bg-white border border-gray-300 h-11 px-3 [&>input]:pt-1"
            />
          </div>

          {/* Description */}
          <div className="flex flex-col w-96">
            <label className="text-lg font-brand-primary mb-1">
              Announcement *
            </label>
            <FilledInput
              multiline
              minRows={4}
              disableUnderline
              placeholder="Full announcement text"
              required
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-xl bg-white border border-gray-300 px-3 py-2"
              maxLength={1000}
            />
            <p className="helperText">
              {"You can use curly brackets {} to indicate Links/URLs e.g. {https://jobhub.cs.vt.edu} becomes "}<a href="https://jobhub.cs.vt.edu" target="_blank" rel="noopener noreferrer">https://jobhub.cs.vt.edu</a>.<br/>
              {"Additionally you can have the link appear as other text by proceeding it with your text and a bar symbol e.g. {link|https://jobhub.cs.vt.edu} becomes "}<a href="https://jobhub.cs.vt.edu" target="_blank" rel="noopener noreferrer">link</a>.<br/>
              {"Of note, all links will open in a new tab and be sure to include the 'https://' or 'http://' at the start of your links or they will not work."}
            </p>
          </div>

          {/* Start/Expire Dates */}
          <div className="flex justify-between w-96">
            <div className="flex flex-col w-44">
              <label className="text-lg font-brand-primary mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="rounded-xl bg-white border border-gray-300 h-11 px-3"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col w-44">
              <label className="text-lg font-brand-primary mb-1">
                Expire Date
              </label>
              <input
                type="date"
                className="rounded-xl bg-white border border-gray-300 h-11 px-3"
                value={expireDate}
                onChange={(e) => setExpireDate(e.target.value)}
              />
            </div>
          </div>

        </DialogContent>

        {/* Footer Buttons */}
        <div className="flex justify-between px-8 mb-4">
          {/* Create Button */}
          <button
          type="button"
          className="primary-button bg-brand-quaternary mb-2 h-11 pt-2 px-6"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Submitting..." : "Create"}
        </button>

          {/* Cancel Button */}
          <button
          type="button"
          className="primary-button bg-brand-primary mb-2 h-11 pt-2 px-6"
          onClick={() => {
            resetForm();
            onClose(false);
          }}
        >
          Cancel
        </button>
        </div>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

AdminAnnouncementsCreate.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired, // onClose(refresh:boolean)
  adminOwnerId: PropTypes.number.isRequired,
};

export default AdminAnnouncementsCreate;
